import { collectionGroup, deleteDoc, doc, getDoc, getDocs, limit, query, setDoc, where } from 'firebase/firestore';
import { db } from '../../../firebase/config';

export interface BranchPinLookupData {
  companyId: string;
  bankId: string;
  branchId: string;
  bankName?: string;
  address?: string;
  branchCode?: string;
  updatedAt?: unknown;
}

/**
 * Doc id = exact pin string. Allows public get-by-id verification without exposing all pins (list stays denied in rules).
 */
export async function syncBranchPinLookup(
  companyId: string,
  bankId: string,
  branchId: string,
  pinCode: string,
  meta?: { bankName?: string; address?: string; branchCode?: string }
): Promise<void> {
  const trimmed = pinCode?.trim();
  if (!trimmed) return;
  const key = trimmed.toUpperCase();
  await setDoc(doc(db, 'branchPinLookup', key), {
    companyId,
    bankId,
    branchId,
    bankName: meta?.bankName ?? '',
    address: meta?.address ?? '',
    branchCode: meta?.branchCode ?? '',
    updatedAt: new Date(),
  });
}

/** Same idea as branchPinLookup, but key is securityCode (e.g. BNK-4584). */
export async function syncSecurityCodeLookup(
  companyId: string,
  bankId: string,
  branchId: string,
  securityCode: string,
  meta?: { bankName?: string; address?: string; branchCode?: string }
): Promise<void> {
  const trimmed = securityCode?.trim();
  if (!trimmed) return;
  const key = trimmed.toUpperCase();
  await setDoc(doc(db, 'securityCodeLookup', key), {
    companyId,
    bankId,
    branchId,
    bankName: meta?.bankName ?? '',
    address: meta?.address ?? '',
    branchCode: meta?.branchCode ?? '',
    updatedAt: new Date(),
  });
}

export async function removeBranchPinLookup(pinCode: string | undefined): Promise<void> {
  const trimmed = pinCode?.trim();
  if (!trimmed) return;
  const key = trimmed.toUpperCase();
  try {
    await deleteDoc(doc(db, 'branchPinLookup', key));
  } catch {
    // ignore if missing
  }
}

export async function removeSecurityCodeLookup(securityCode: string | undefined): Promise<void> {
  const trimmed = securityCode?.trim();
  if (!trimmed) return;
  const key = trimmed.toUpperCase();
  try {
    await deleteDoc(doc(db, 'securityCodeLookup', key));
  } catch {
    // ignore if missing
  }
}

export async function verifyBranchPinForPath(
  enteredPin: string,
  companyId: string,
  bankId: string,
  branchId: string
): Promise<BranchPinLookupData | null> {
  const trimmed = enteredPin.trim();
  if (!trimmed) return null;
  const key = trimmed.toUpperCase();
  const snap = await getDoc(doc(db, 'branchPinLookup', key));
  if (!snap.exists()) return null;
  const d = snap.data() as BranchPinLookupData;
  if (d.companyId === companyId && d.bankId === bankId && d.branchId === branchId) {
    return d;
  }
  return null;
}

/** Public verification by PIN only (used by /complain route). */
export async function verifyBranchPin(enteredPin: string): Promise<BranchPinLookupData | null> {
  const trimmed = enteredPin.trim();
  if (!trimmed) return null;
  const key = trimmed.toUpperCase();
  const snap = await getDoc(doc(db, 'branchPinLookup', key));
  if (!snap.exists()) return null;
  return snap.data() as BranchPinLookupData;
}

export async function verifySecurityCode(code: string): Promise<BranchPinLookupData | null> {
  const trimmed = code.trim();
  if (!trimmed) return null;
  const key = trimmed.toUpperCase();
  const snap = await getDoc(doc(db, 'securityCodeLookup', key));
  if (!snap.exists()) return null;
  return snap.data() as BranchPinLookupData;
}

/**
 * Fallback (less secure): query branches by pinCode/securityCode.
 * Requires Firestore rules that allow list/query on branches. Prefer lookup docs for production.
 */
export async function findBranchByCodeViaQuery(code: string): Promise<BranchPinLookupData | null> {
  const trimmed = code.trim();
  if (!trimmed) return null;
  const key = trimmed.toUpperCase();

  const branches = collectionGroup(db, 'branches');

  // Try pinCode match
  const byPinQ = query(branches, where('pinCode', '==', key), limit(1));
  const byPinSnap = await getDocs(byPinQ);
  if (!byPinSnap.empty) {
    const d = byPinSnap.docs[0];
    return extractLookupFromBranchDoc(d.ref.path, d.data());
  }

  // Try securityCode match
  const bySecQ = query(branches, where('securityCode', '==', key), limit(1));
  const bySecSnap = await getDocs(bySecQ);
  if (!bySecSnap.empty) {
    const d = bySecSnap.docs[0];
    return extractLookupFromBranchDoc(d.ref.path, d.data());
  }

  return null;
}

function extractLookupFromBranchDoc(path: string, data: any): BranchPinLookupData | null {
  // bankClient/{companyId}/banks/{bankId}/branches/{branchId}
  const parts = path.split('/');
  const idx = parts.indexOf('bankClient');
  if (idx < 0) return null;
  const companyId = parts[idx + 1];
  const bankId = parts[idx + 3];
  const branchId = parts[idx + 5];
  if (!companyId || !bankId || !branchId) return null;

  return {
    companyId,
    bankId,
    branchId,
    bankName: data?.bankName ?? '',
    address: data?.address ?? '',
    branchCode: data?.branchCode != null ? String(data.branchCode) : '',
  };
}
